import { forwardRef, ButtonHTMLAttributes } from 'react';
import { Video as LucideIcon } from 'lucide-react';

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: LucideIcon;
  label: string;
  variant?: 'default' | 'danger' | 'success';
  size?: 'sm' | 'md';
}

const variantClasses = {
  default: 'text-gray-600 hover:text-gray-900 hover:bg-gray-100',
  danger: 'text-red-600 hover:text-red-700 hover:bg-red-50',
  success: 'text-green-600 hover:text-green-700 hover:bg-green-50'
};

const sizeClasses = {
  sm: 'p-1.5',
  md: 'p-2'
};

const iconSizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5'
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon: Icon, label, variant = 'default', size = 'md', className = '', ...props }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        title={label}
        className={`
          inline-flex items-center justify-center
          rounded-lg
          transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2
          disabled:opacity-50 disabled:cursor-not-allowed
          ${variantClasses[variant]}
          ${sizeClasses[size]}
          ${className}
        `}
        {...props}
      >
        <Icon className={iconSizeClasses[size]} />
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';
